import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {useRouteOrganizationEventId} from 'utils/lib/url'
import {toast} from 'react-toastify'
import {Attendee} from 'attendee/models/attendee'
import {Product} from 'organization/Event/Products/EventProductsProvider'

export type ProductPurchase = {
  attendee: Attendee
  product: Product
  id: number
  address: string
  phone_number: string
  name: string
  status: string
  price: number
  info: string
}

export interface EventProductPurchasesContextProps {
  purchases: ProductPurchase[]
  loading: boolean
  page: number
  totalPages: number
  changePage: (e: any, page: number) => void
  updatePurchase: (productPurchase: ProductPurchase) => Promise<boolean>
}

export const EventProductPurchasesContext = React.createContext<
  EventProductPurchasesContextProps | undefined
>(undefined)

export default function EventProductPurchasesProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [purchases, setPurchases] = useState<ProductPurchase[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async (getPage?: number) => {
    setLoading(true)
    const selectedPage = getPage ?? page

    await apiClient
      .get(`api/event/${eventId}/products/purchases?page=${selectedPage}`)
      .then((response) => {
        setPurchases(response.data)
        setTotalPages(response.last_page)
        setPage(response.current_page)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const changePage = (e: any, toPage: number) => {
    setPage(toPage)
    get(toPage)
  }

  const updatePurchase = async (productPurchase: ProductPurchase) => {
    setLoading(true)

    const result = await apiClient
      .post(
        `api/event/${eventId}/products/purchases/${productPurchase.id}`,
        productPurchase,
      )
      // .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
      })

    return result
  }

  return (
    <EventProductPurchasesContext.Provider
      value={{
        purchases,
        loading,
        page,
        changePage,
        totalPages,
        updatePurchase,
      }}
    >
      {props.children}
    </EventProductPurchasesContext.Provider>
  )
}

export function useEventProductPurchases() {
  const context = React.useContext(EventProductPurchasesContext)
  if (context === undefined) {
    throw new Error(
      'useEventProductPurchases must be used within EventProductPurchasesProvider',
    )
  }
  return context
}

export function purchaseStatusLabel(
  status: string,
  t: (key: string) => string,
): string {
  const key = `orgPurchases.status.${status}`
  const v = t(key)
  return v !== key ? v : status
}
